import { useState, useCallback } from 'react';

import { importMobileDevices } from '../../apiClient';
import { AsyncTask } from '../../utils/asyncTasks';

/**
 * Custom hook for mobile devices upload operations
 */
export const parseErrorResponse = async (res: Response): Promise<string> => {
    try {
        const data = await res.json();
        // Common NestJS error shape: { statusCode, message, error }
        if (typeof data?.message === 'string') return data.message as string;
        if (Array.isArray(data?.message)) return (data.message as string[]).join(', ');
        if (typeof data?.error === 'string') return data.error as string;
        return JSON.stringify(data);
    } catch {
        return `Request failed with status ${res.status}`;
    }
};

export const useMobileDevicesUpload = (setTaskStateError: (string) => void) => {
    const [uploading, setUploading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Upload a mobile devices file
     */
    const uploadFile = useCallback(
        async (file: File, dryRun?: boolean, dryRunAsyncTaskId?: string): Promise<AsyncTask> => {
            setUploading(true);
            setError(null);

            try {
                const params = {
                    data: file,
                    dryRun,
                    dryRunAsyncTaskId,
                } as const;

                // importMobileDevices returns parsed JSON on success and throws a Response on failure
                const asyncTask: AsyncTask = await importMobileDevices(params);
                setUploading(false);
                return asyncTask; // Return the task for tracking
            } catch (err: unknown) {
                let errorMessage = 'Failed to upload file';
                if (err instanceof Response) {
                    try {
                        errorMessage = await parseErrorResponse(err);
                    } catch {
                        errorMessage = `Request failed with status ${err.status}`;
                    }
                } else if (err instanceof Error && err.message) {
                    errorMessage = err.message;
                }
                setUploading(false);
                setError(errorMessage);
                setTaskStateError(errorMessage);
                throw err;
            }
        },
        []
    );

    return {
        uploading,
        error,
        setError,
        uploadFile,
    };
};
